import { Alert, Box, Button, Group, Skeleton, Stack } from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { notifyError } from '@/shared/notify';

interface RecordingPlayerProps {
    loadUrl: (download?: boolean) => Promise<string>;
}

export function RecordingPlayer({ loadUrl }: Readonly<RecordingPlayerProps>) {
    const { t } = useTranslation('recordings');
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const loadedUrlRef = useRef<string | null>(null);
    const resumeAtRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        loadUrl()
            .then((nextUrl) => {
                if (!cancelled) setUrl(nextUrl);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setFailed(true);
                notifyError(t('errors.url'), error);
            });
        return () => {
            cancelled = true;
        };
    }, [loadUrl, t]);

    const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
        const video = event.currentTarget;
        loadedUrlRef.current = video.currentSrc;
        if (resumeAtRef.current === null) return;
        video.currentTime = resumeAtRef.current;
        resumeAtRef.current = null;
        void video.play();
    };

    const handleError = (event: SyntheticEvent<HTMLVideoElement>) => {
        const video = event.currentTarget;
        if (loadedUrlRef.current !== video.currentSrc) {
            setFailed(true);
            notifyError(t('errors.playback'));
            return;
        }
        resumeAtRef.current = video.currentTime;
        loadedUrlRef.current = null;
        loadUrl()
            .then(setUrl)
            .catch((error: unknown) => {
                setFailed(true);
                notifyError(t('errors.url'), error);
            });
    };

    const download = async () => {
        setDownloading(true);
        try {
            const link = document.createElement('a');
            link.href = await loadUrl(true);
            document.body.append(link);
            link.click();
            link.remove();
        } catch (error) {
            notifyError(t('errors.download'), error);
        } finally {
            setDownloading(false);
        }
    };

    if (failed) {
        return (
            <Alert color="danger" icon={<IconAlertTriangle size={20} />}>
                {t('errors.playback')}
            </Alert>
        );
    }

    return (
        <Stack gap="md">
            {url ? (
                <Box
                    autoPlay
                    controls
                    component="video"
                    display="block"
                    src={url}
                    w="100%"
                    style={{
                        borderRadius: 'var(--radius-md)',
                        aspectRatio: '16 / 9',
                        background: 'var(--color-background-default)',
                    }}
                    onError={handleError}
                    onLoadedMetadata={handleLoadedMetadata}
                />
            ) : (
                <Skeleton style={{ aspectRatio: '16 / 9' }} />
            )}
            <Group justify="flex-end">
                <Button
                    disabled={!url}
                    leftSection={<IconDownload size={20} />}
                    loading={downloading}
                    variant="light"
                    onClick={() => void download()}
                >
                    {t('download')}
                </Button>
            </Group>
        </Stack>
    );
}
